import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ButtonWithIcon } from '@ff-os/shared-react/components/buttons/button-with-icon/ButtonWithIcon';
import { Button } from '@ff-os/shared-react/components/buttons/button/Button';
import { WindowFooter } from '@ff-os/shared-react/components/window/partials/WindowFooter';
import { useExternalTotalRender } from '@ff-os/shared-react/hooks/useExternalTotalRender';
import { selectActiveVariantSku } from '@ff-scene-plugins/gift-redux/selectors/variantSelectors';
import { GiftOrderButton } from '../../../order-button/GiftOrderButton';
import styles from './GiftWindowFooter.module.scss';

const PreviousButton = ({
    onClose,
    previous,
    previousButtonText,
    showPrevious
}: PreviousButtonProps) => {
    const { t } = useTranslation();

    if (!showPrevious) {
        return <div />;
    }

    if (previous === undefined) {
        return (
            <Button className={styles.button} style='outline' onClick={onClose}>
                {t('commons.close')}
            </Button>
        );
    }

    if (previous) {
        return (
            <ButtonWithIcon className={styles.button}
                icon='general-ui-chevron-left' position='left' style='outline'
                onClick={previous}>
                {previousButtonText}
            </ButtonWithIcon>
        );
    }

    return <div />;
};

type PreviousButtonProps = {
    onClose: () => void;
    previous: (() => void) | undefined;
    previousButtonText: string | undefined;
    showPrevious: boolean;
};

const ContentFirstPrice = ({ quantity }: { quantity?: number }) => {
    const sku = useSelector(selectActiveVariantSku);
    const priceRequest = React.useMemo(() => ({
        items: sku ? [{ sku, quantity: quantity ?? 1 }] : []
    }), [sku, quantity]);

    const { destinationRef, plainText } = useExternalTotalRender(priceRequest);

    if (!sku) {
        return null;
    }

    return (
        <div ref={destinationRef} className={styles.priceContainer}>
            {plainText && <span className={styles.price}>{plainText}</span>}
        </div>
    );
};

export const GiftWindowFooter = ({
    onClose, next, previous,
    quantity,
    nextButtonText, previousButtonText,
    contentFirst,
    showPrevious = true
}: Props) => {
    return (
        <WindowFooter>
            <PreviousButton
                onClose={onClose}
                previous={previous}
                previousButtonText={previousButtonText}
                showPrevious={showPrevious}
            />

            {contentFirst && !next ? (
                <div className={styles.contentFirstRight}>
                    <ContentFirstPrice quantity={quantity} />
                    <GiftOrderButton className={styles.button} quantity={quantity} contentFirst={contentFirst} />
                </div>
            ) : next ? (
                <ButtonWithIcon className={styles.button}
                    icon='general-ui-chevron-right'
                    position='right'
                    onClick={next}>
                    {nextButtonText}
                </ButtonWithIcon>
            ) : (
                <GiftOrderButton className={styles.button} quantity={quantity} contentFirst={contentFirst} />
            )}
        </WindowFooter>
    );
};

type Props = {
    quantity?: number;
    onClose: () => void;
    previous: (() => void) | undefined;
    showPrevious?: boolean;
    previousButtonText: string | undefined;
    next: (() => void) | undefined;
    nextButtonText: string | undefined;
    contentFirst?: boolean;
};